import { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useMemoizedFn } from 'ahooks'

/**
 * 合并额外数据到基础列表数据
 * @param {boolean} overrideBaseList 是否覆盖基础列表数据
 */
const mergeExtraDataInBaseList = ({
    overrideBaseList,
    fetchMap,
    fetchKey,
    baseList,
    rowKey,
    extraData,
    params,
    fullExtraData
}) => {
    // 覆盖基础列表数据
    if (overrideBaseList) {
        const merge = fetchMap[fetchKey]?.merge

        if (!rowKey || typeof merge !== 'function') {
            return {
                mergedList: baseList
            }
        }

        let hasItemUpdate = false

        // 此处使用了原始列表数据，如果中途有变更会丢掉，先这样了，后面再说
        const newListData = baseList.map(v => {
            const mergeData = merge({
                rowKey,
                baseData: v,
                extraData,
                params,
                fullExtraData
            })

            // 只更新需要更新的字段，如果返回的数据为空，则不更新
            const hasUpdate = mergeData && mergeData !== v

            if (hasUpdate) {
                hasItemUpdate = true // 标记存在更新

                Object.assign(v, mergeData) // 注意：此处直接修改了原数据

                return {
                    ...v,
                    ...mergeData
                }
            }

            return v
        })

        if (hasItemUpdate) {
            return {
                hasItemUpdate,
                mergedList: newListData
            }
        }

        return {
            mergedList: baseList
        }
    }

    const list = Object.keys(fetchMap).filter(v => fetchMap[v]?.merge)

    const newListData = baseList.map(v => {
        const extData = list.reduce((t, c) => {
            const item = fetchMap[c]

            const mergeData = item.merge({
                rowKey,
                baseData: v,
                extraData: fullExtraData?.[c],
                params,
                fullExtraData
            })

            // 只更新需要更新的字段，如果返回的数据为空，则不更新
            const hasUpdate = mergeData && mergeData !== t

            if (hasUpdate) {
                return {
                    ...t,
                    ...mergeData
                }
            }

            return t
        }, v)

        if (extData !== v) {
            return {
                ...v,
                ...extData
            }
        }

        return v
    })

    return {
        mergedList: newListData
    }
}

/**
 * 额外数据分为两种：1. 列表补充数据；2. 完全独立出去的数据
 * 列表相关直接
 */
export default ({ fetchMap, rowKey, onLoadedData }) => {
    const [extraData, setExtraData] = useState(null)
    const fetchIdRef = useRef(false)
    const statusMapRef = useRef(null)
    const cacheDataRef = useRef({})

    if (!statusMapRef.current) {
        statusMapRef.current = new Map()
    }

    useEffect(() => {
        return () => {
            fetchIdRef.current = 0
        }
    }, [])

    /**
     * 检查任务是否完成
     */
    const checkCanProcess = useMemoizedFn(({ fetchMap, fetchKey }) => {
        const task = fetchMap[fetchKey]

        // 已完成
        if (statusMapRef.current?.has(fetchKey)) {
            return false
        }

        // 存在依赖，需要等到依赖项加载完成，才触发
        const deps = task?.deps

        // 无依赖，立即执行
        if (!deps?.length) {
            return true
        }

        // 所有依赖执行完成才执行
        return deps.every(v => statusMapRef.current?.has(v))
    })

    const loadExtraData = useMemoizedFn(({ fetchId: inputFetchId, baseList, params, fetchKeys }) => {
        if (!fetchKeys?.length) {
            statusMapRef.current.clear()
            cacheDataRef.current = {}
        }

        if (!fetchMap || !baseList?.length) {
            return
        }

        const fetchId = typeof inputFetchId !== 'undefined' ? inputFetchId : fetchIdRef.current + 1

        fetchIdRef.current = fetchId

        const requestIdleCallback = fn => {
            const wrapFn = () => {
                if (fetchId !== fetchIdRef.current) {
                    return
                }

                fn()
            }

            if (window.requestIdleCallback) {
                window.requestIdleCallback(wrapFn)
                return
            }

            setTimeout(wrapFn)
        }

        const map = typeof fetchMap === 'function' ? fetchMap({ baseList, fetchId, params }) : fetchMap

        const arr = Object.keys(map)

        arr.forEach(async v => {
            if (fetchKeys && !fetchKeys.includes(v)) {
                return
            }

            const canProcess = checkCanProcess({ fetchMap: map, fetchKey: v })

            if (!canProcess) {
                return
            }

            const fn = (typeof map[v] === 'function' && map[v]) || (typeof map[v].fetch === 'function' && map[v].fetch)

            if (!fn) {
                console.error('缺少数据获取方法!')
                return
            }

            const res = await fn({ baseList, params, fullExtraData: cacheDataRef.current })

            requestIdleCallback(() => {
                ReactDOM.unstable_batchedUpdates(() => {
                    cacheDataRef.current[v] = res

                    setExtraData(prev => ({
                        ...(prev || {}),
                        [v]: res
                    }))

                    const obj = {
                        fetchId,
                        baseList,
                        extraData: res,
                        params,
                        fetchMap: map,
                        fetchKey: v,
                        fullExtraData: cacheDataRef.current,
                        gerMergedList({ overrideBaseList }) {
                            return mergeExtraDataInBaseList({
                                fetchMap: map,
                                fetchKey: v,
                                baseList,
                                rowKey,
                                extraData: res,
                                params,
                                fullExtraData: cacheDataRef.current,
                                overrideBaseList
                            })
                        }
                    }

                    onLoadedData && onLoadedData(obj)

                    statusMapRef.current.set(v, obj)

                    // 存在依赖当前项且目标项的所有依赖项都已加载完成
                    const childKeys = arr
                        .filter(v1 => map[v1]?.deps?.includes?.(v))
                        .filter(v1 => map[v1].deps.every(v2 => statusMapRef.current.has(v2)))

                    if (childKeys?.length) {
                        loadExtraData({ fetchId, baseList, params, fetchKeys: childKeys })
                    }
                })
            })
        })
    })

    const updateExtraData = useMemoizedFn(({ fieldKey, fieldValue }) => {
        cacheDataRef.current[fieldKey] = fieldValue

        setExtraData(prev => {
            return {
                ...prev,
                [fieldKey]: fieldValue
            }
        })
    })

    return { extraData, loadExtraData, updateExtraData }
}
